import React, { useEffect, useState } from "react";
import { isViewEnabled } from "../features";

function MenuIcon({ open }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
      {open ? (
        <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.17 12l6.72-7.71 1.41 1.42z" fill="currentColor" />
      ) : (
        <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" fill="currentColor" />
      )}
    </svg>
  );
}

const LEGACY_EXPENSE_VIEW = "miscExpenses";

const DELIVERY_VIEWS = [
  { id: "billing", label: "Delivery Billing" },
  { id: "billingReport", label: "Delivery Billing Report" },
  { id: "billingSummary", label: "Summary Report" },
  { id: "expenseDelivery", label: "Delivery Expenses" },
];

const STALL_VIEWS = [
  { id: "salesStall", label: "Stall Billing" },
  { id: "stallReports", label: "Stall Reports" },
  { id: "inwardStall", label: "Stall - Inward & Sales" },
  { id: "expenseStall", label: "Stall Expenses" },
];

const MARKET_VIEWS = [
  { id: "salesMarket", label: "Market Credit Sales" },
  { id: "inwardMarket", label: "Market - Material Inward" },
  { id: "marketReports", label: "Market Reports" },
  { id: "expenseMarket", label: "Market Expenses" },
];

const MASTER_VIEWS = [
  { id: "storeMaster", label: "Store Master" },
  { id: "itemMaster", label: "Item Master" },
  { id: "supplierMaster", label: "Supplier Master" },
  { id: "customerMaster", label: "Customer Master" },
  { id: "expenseNameMaster", label: "Expense Master" },
  { id: "employeeMaster", label: "Employee Master" },
  { id: "employeeAttendance", label: "Employee Attendance" },
  { id: "expenseMaster", label: "Master Expense" },
];

const VIEW_LABELS = {
  dashboard: "Dashboard",
  amountReceived: "Daily Amount Received",
  inward: "Material Purchase",
  purchaseReports: "Material Purchase Report",
  billing: "Delivery Billing",
  billingReport: "Delivery Billing Report",
  billingSummary: "Summary Report",
  expenseDelivery: "Delivery Expenses",
  expenseStall: "Stall Expenses",
  expenseMarket: "Market Expenses",
  expenseMaster: "Master Expense",
  salesStall: "Stall Billing",
  stallReports: "Stall Reports",
  salesMarket: "Market Credit Sales",
  marketReports: "Market Reports",
  inwardStall: "Stall - Inward & Sales",
  inwardMarket: "Market - Material Inward",
  itemMaster: "Item Master",
  supplierMaster: "Supplier Master",
  customerMaster: "Customer Master",
  storeMaster: "Store Master",
  expenseNameMaster: "Expense Master",
  employeeMaster: "Employee Master",
  employeeAttendance: "Employee Attendance",
  userAccess: "User Access",
  billRequests: "Approval Requests",
  miscExpenses: "Delivery Expenses",
  noAccess: "No Access",
};

function viewTitle(view) {
  return VIEW_LABELS[view] || "";
}

function formatLiveDateTime(value) {
  const date = new Date(value);
  const dateText = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(date);
  const timeText = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  }).format(date);
  return { dateText, timeText };
}

export default function Layout({ currentView, onNavigate, currentUser, onLogout, children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(true);
  const [stallOpen, setStallOpen] = useState(true);
  const [marketOpen, setMarketOpen] = useState(true);
  const [mastersOpen, setMastersOpen] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const isAdmin = (currentUser?.role || "") === "admin";
  const allowedViews = Array.isArray(currentUser?.allowedViews) ? currentUser.allowedViews : [];

  const canAccess = (view) =>
    isViewEnabled(view) && (view !== "storeMaster" || isAdmin) && (isAdmin ||
    allowedViews.includes(view === "billingSummary" ? "billingReport" : view) ||
    (
      (view === "expenseDelivery" || view === "expenseStall" || view === "expenseMarket" || view === "expenseMaster") &&
      allowedViews.includes(LEGACY_EXPENSE_VIEW)
    ));

  const visibleDelivery = DELIVERY_VIEWS.filter((item) => canAccess(item.id));
  const visibleStall = STALL_VIEWS.filter((item) => canAccess(item.id));
  const visibleMarket = MARKET_VIEWS.filter((item) => canAccess(item.id));
  const visibleMasters = MASTER_VIEWS.filter((item) => canAccess(item.id));

  const deliveryActive =
    currentView === LEGACY_EXPENSE_VIEW || visibleDelivery.some((item) => item.id === currentView);
  const stallActive = visibleStall.some((item) => item.id === currentView);
  const marketActive = visibleMarket.some((item) => item.id === currentView);
  const mastersActive = visibleMasters.some((item) => item.id === currentView);

  const go = (view) => {
    onNavigate(view);
    setDrawerOpen(false);
  };
  const brandLogoSrc = `${process.env.PUBLIC_URL || ""}/Logo.png`;
  const { dateText, timeText } = formatLiveDateTime(now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const renderGroup = (title, items, open, setOpen, active, tone = "") => {
    if (!items.length) return null;
    return (
      <div className={`nav-group nav-group-card ${tone ? `nav-group-card-${tone}` : ""}`}>
        <button
          type="button"
          className={`nav-item nav-group-trigger ${active ? "active" : ""}`}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <span className="nav-group-text">
            {tone ? <span className={`nav-group-icon nav-group-icon-${tone}`} aria-hidden /> : null}
            <span className="nav-group-kicker">Section</span>
            <span className="nav-group-title">{title}</span>
          </span>
          <span className={`caret ${open ? "open" : ""}`} aria-hidden>
            &gt;
          </span>
        </button>
        {open ? (
          <div className="nav-sub">
            {items.map((item) => {
              const isLegacyDeliveryExpense = currentView === LEGACY_EXPENSE_VIEW && item.id === "expenseDelivery";
              const isActive = currentView === item.id || isLegacyDeliveryExpense;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`nav-item sub ${isActive ? "active" : ""}`}
                  onClick={() => go(item.id)}
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`layout ${drawerOpen ? "drawer-open" : ""}`}>
      <header className="topbar">
        <button className="icon-btn" onClick={() => setDrawerOpen((v) => !v)} aria-label="Toggle menu">
          <MenuIcon open={drawerOpen} />
        </button>
        <div className="brand-identity">
          <div className="brand-logo-wrap">
            <img className="brand-logo" src={brandLogoSrc} alt="Alif Protiens logo" />
          </div>
          <div className="brand-lockup">
            <div className="brand">Alif Protiens</div>
            <div className="brand-subtitle">Operations Console</div>
          </div>
        </div>
        <span className="divider" aria-hidden>
          |
        </span>
        <div className="top-title" role="heading" aria-level={1}>
          {viewTitle(currentView)}
        </div>
        <div className="topbar-right">
          <div className="date-time-pill" aria-label={`Current date ${dateText} and time ${timeText}`}>
            <span className="date-time-date">{dateText}</span>
            <span className="date-time-sep">|</span>
            <span className="date-time-time">{timeText}</span>
          </div>
          {currentUser ? <div className="user-pill">{currentUser.username}</div> : null}
          {onLogout ? (
            <button type="button" className="topbar-btn" onClick={onLogout}>
              Logout
            </button>
          ) : null}
        </div>
      </header>

      <aside className="drawer">
        <div className="drawer-header">
          <div className="drawer-kicker">Navigation</div>
          <div className="drawer-title-card">Choose a work area</div>
          <div className="drawer-copy">Fast access to daily operations, reporting, and approvals.</div>
        </div>
        <nav className="nav">
          {canAccess("dashboard") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "dashboard" ? "active" : ""}`}
              onClick={() => go("dashboard")}
              aria-current={currentView === "dashboard" ? "page" : undefined}
            >
              Dashboard
            </button>
          ) : null}

          {isAdmin ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "amountReceived" ? "active" : ""}`}
              onClick={() => go("amountReceived")}
              aria-current={currentView === "amountReceived" ? "page" : undefined}
            >
              Daily Amount Received
            </button>
          ) : null}

          {canAccess("inward") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "inward" ? "active" : ""}`}
              onClick={() => go("inward")}
              aria-current={currentView === "inward" ? "page" : undefined}
            >
              Material Purchase
            </button>
          ) : null}

          {canAccess("purchaseReports") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "purchaseReports" ? "active" : ""}`}
              onClick={() => go("purchaseReports")}
              aria-current={currentView === "purchaseReports" ? "page" : undefined}
            >
              Material Purchase Report
            </button>
          ) : null}

          {renderGroup("Delivery", visibleDelivery, deliveryOpen, setDeliveryOpen, deliveryActive, "delivery")}
          {renderGroup("Stall", visibleStall, stallOpen, setStallOpen, stallActive, "stall")}
          {renderGroup("Market", visibleMarket, marketOpen, setMarketOpen, marketActive, "market")}
          {renderGroup("Masters", visibleMasters, mastersOpen, setMastersOpen, mastersActive)}

          {isAdmin ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "userAccess" ? "active" : ""}`}
              onClick={() => go("userAccess")}
              aria-current={currentView === "userAccess" ? "page" : undefined}
            >
              User Access
            </button>
          ) : null}

          {isAdmin ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "billRequests" ? "active" : ""}`}
              onClick={() => go("billRequests")}
              aria-current={currentView === "billRequests" ? "page" : undefined}
            >
              Approval Requests
            </button>
          ) : null}
        </nav>
      </aside>

      <div className="overlay" onClick={() => setDrawerOpen(false)} />

      <main className="content">{children}</main>
      <footer className="footer">© {new Date().getFullYear()} Alif Protiens</footer>
    </div>
  );
}
